/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/lazy-aggrid-impl.tsx
 * FILE_ID: QB-AGGRID-LAZY-001
 * PURPOSE: Lazy-loaded AG-Grid React wrapper. Owns the heavy bundle
 *          import + the keyboard / clipboard / focus event handling
 *          that translates AG-Grid's native events into the Bravada
 *          section editor's persist callbacks.
 * WHY: Split out from ScopeAgGridSection so the section component
 *      stays under the 300-line cap and the AG-Grid bundle (~600KB
 *      minified) doesn't load on non-builder routes.
 * SIZE WAIVER (>300 lines): this file is a single cohesive AG-Grid
 *      wrapper — the lazy bundle import, the props contract, and the
 *      event-handling hook composition form one tight responsibility.
 *      Splitting it further would force props to round-trip through
 *      another module without buying any reuse. The handlers it owns
 *      (keyboard, clipboard, focus, paste) already live in their own
 *      hook files; what remains here is the wiring that must stay
 *      colocated with the AgGridReact mount.
 * RELATED:
 *   - QB-AGGRID-SECTION-001 (ScopeAgGridSection.tsx) — consumer
 *   - QB-AGGRID-COLUMNS-001 (scope-ag-grid-columns.ts) — column schema
 *   - QB-AGGRID-APPLICATION-PICKER-001 (use-scope-ag-application-picker.ts)
 */

import { Suspense, lazy, useCallback, useEffect, useMemo, useRef } from 'react'
import { Loader2 } from 'lucide-react'
import type { QuoteLineItem } from '@/lib/quoteApi'
import type { AgColGroupDef } from './scope-ag-grid-columns'
import { isPadRowId } from './pad-row-helpers'
import ScopeAgGridStyles from './lazy-aggrid-styles'
import {
  useAgGridKeyboardShortcuts,
  useAgGridContextMenu,
} from './useAgGridKeyboardShortcuts'
import { usePadFocusAdvance } from './usePadFocusAdvance'

export type ScopeGridDensity = 'comfortable' | 'compact'

export interface ScopeGridFocusRequest {
  nonce: number
  field: string
  rowId?: string
  minRealRowCount?: number
  startEditing?: boolean
}

export interface LazyAgGridProps {
  rows: QuoteLineItem[]
  columnDefs: AgColGroupDef[]
  editableFieldIds: string[]
  isEditable: boolean
  /** SAM-style density toggle. Comfortable = 32px rows, Compact = 24px. */
  density?: ScopeGridDensity
  /**
   * When this number changes the grid resets all column state
   * (widths, ordering) back to its declared defaults. Used by the
   * toolbar "Reset widths" button — incrementing the counter is the
   * trigger.
   */
  resetSignal?: number
  onCellEdit: (itemId: string, field: string, value: unknown) => void
  onPasteRange: (
    startItemId: string,
    startField: string,
    cells: string[][],
  ) => void
  onFillDown: (
    sourceItemId: string,
    targetItemIds: string[],
    fields: string[],
  ) => void
  onFocusChange: (
    itemId: string | null,
    field: string | null,
    value: string,
  ) => void
  /**
   * SAM-style pad promotion: invoked once when the user commits the
   * first cell on a trailing blank pad row. The parent is expected to
   * persist a new line item with the given field=value as the seed,
   * then refetch — the new real row replaces the pad in the next render.
   */
  onPromotePadRow?: (padId: string, field: string, value: unknown) => void
  /**
   * SAM-style per-row Delete action. Fired by the right-hand actions
   * column's trash icon.
   */
  onDeleteRow?: (rowId: string) => void
  /**
   * SAM-style per-row Duplicate action. Fired by the right-hand
   * actions column's copy icon. Parent should clone the row's payload
   * and POST it as a new line item directly below.
   */
  onDuplicateRow?: (row: QuoteLineItem) => void
  /**
   * Bravada pricelist takeoff (MR4). Fired by the right-hand actions
   * column's Apply-application icon. When defined, the parent opens
   * the ApplicationSelectorModal and PATCHes `application_id` on
   * select; backend Application.apply_application then autofills the
   * row's product slots, product_system, and productivity_pmpd.
   * Left undefined for trade modes without an application catalogue.
   */
  onApplyApplication?: (row: QuoteLineItem) => void
  /**
   * SAM-style right-click handler. Fired by AG-Grid's
   * onCellContextMenu event; receives the clicked row's id + the
   * native pointer coordinates so the section can render the popup
   * exactly where the user clicked.
   */
  onRowContextMenu?: (rowId: string, x: number, y: number) => void
  /** Imperative focus request from adjacent controls such as fast-add. */
  focusRequest?: ScopeGridFocusRequest | null
  /** Called after the next committed quantity edit in the grid. */
  onQuantityCommitted?: () => void
}

const LazyAgGridImpl = lazy(async () => {
  const [{ AgGridReact }, agCore] = await Promise.all([
    import('ag-grid-react'),
    import('ag-grid-community'),
    import('ag-grid-community/styles/ag-grid.css'),
    import('ag-grid-community/styles/ag-theme-quartz.css'),
  ])
  // AG-Grid v32: ClientSideRowModel + range selection + clipboard ship
  // by default. Reference held so a future v33 module-explicit upgrade
  // is a one-line swap here.
  void agCore

  type GridApi = import('ag-grid-community').GridApi<QuoteLineItem>
  type CellValueChangedEvent =
    import('ag-grid-community').CellValueChangedEvent<QuoteLineItem>
  type ProcessDataFromClipboardParams =
    import('ag-grid-community').ProcessDataFromClipboardParams<QuoteLineItem>
  function Inner(props: LazyAgGridProps) {
    const {
      rows,
      columnDefs,
      editableFieldIds,
      isEditable,
      density = 'comfortable',
      resetSignal = 0,
      onCellEdit,
      onPasteRange,
      onFillDown,
      onFocusChange,
      onPromotePadRow,
      onDeleteRow,
      onDuplicateRow,
      onApplyApplication,
      onRowContextMenu,
      focusRequest,
      onQuantityCommitted,
    } = props
    const apiRef = useRef<GridApi | null>(null)
    const handledFocusNonceRef = useRef<number | null>(null)

    // AG-Grid passes `context` to every cellRenderer. We use it to
    // give the row-actions renderer (Apply / Copy / Delete icons)
    // access to the section's persist callbacks without prop-drilling
    // through AG-Grid's column-def opaque schema.
    const gridContext = useMemo(
      () => ({
        onDuplicate: (row: QuoteLineItem) => onDuplicateRow?.(row),
        onDelete: (rowId: string) => onDeleteRow?.(rowId),
        // Only forwarded when the parent supplied a handler — keeps
        // `onApplyApplication` off the context for trade modes that
        // shouldn't expose the button (e.g. steel/SAM today).
        onApplyApplication: onApplyApplication
          ? (row: QuoteLineItem) => onApplyApplication(row)
          : undefined,
        isEditable,
      }),
      [onDuplicateRow, onDeleteRow, onApplyApplication, isEditable],
    )

    // SAM-style "Reset widths" — parent bumps resetSignal to clear
    // saved column widths/ordering. resetColumnState is best-effort
    // (older AG-Grid bundles may not expose it).
    useEffect(() => {
      if (resetSignal === 0) return
      const api = apiRef.current
      if (!api) return
      try {
        api.resetColumnState?.()
        api.sizeColumnsToFit?.()
      } catch {
        // soft no-op
      }
    }, [resetSignal])

    // Ensure the grid mounts with the leftmost columns visible. Guards
    // against stale horizontal scroll restore (bf-cache, lazy rehydrate)
    // dropping the user on the Advanced band with inputs off-screen.
    const handleGridReady = useCallback(
      (p: { api: GridApi }) => {
        apiRef.current = p.api as unknown as GridApi
        try {
          p.api.ensureColumnVisible?.('description', 'start')
        } catch {
          // soft no-op if Description column has been hidden
        }
      },
      [],
    )

    // SAM-style "after promotion, focus the next blank pad". When a
    // pad-row commit fires, mark a pending advance — the effect inside
    // the hook fires it once the new real row lands in rows[].
    const { markPendingAdvance } = usePadFocusAdvance(rows, apiRef as never)

    useEffect(() => {
      if (!focusRequest) return
      if (handledFocusNonceRef.current === focusRequest.nonce) return
      const api = apiRef.current
      if (!api) return
      const realRows = rows.filter((row) => !isPadRowId(row.id))
      if (
        focusRequest.minRealRowCount != null &&
        realRows.length < focusRequest.minRealRowCount
      ) {
        return
      }
      const rowIndex =
        focusRequest.rowId != null
          ? rows.findIndex((row) => row.id === focusRequest.rowId)
          : realRows.length - 1
      if (rowIndex < 0) return
      try {
        api.ensureColumnVisible?.(focusRequest.field, 'middle')
        api.ensureIndexVisible?.(rowIndex)
        api.setFocusedCell(rowIndex, focusRequest.field)
        if (focusRequest.startEditing) {
          api.startEditingCell?.({
            rowIndex,
            colKey: focusRequest.field,
          })
        }
        handledFocusNonceRef.current = focusRequest.nonce
      } catch {
        // Column or row may have disappeared during a refetch; soft no-op.
      }
    }, [focusRequest, rows])

    const handleCellValueChanged = useCallback(
      (evt: CellValueChangedEvent) => {
        const colId = evt.column.getColId()
        if (!colId || !evt.data?.id) return
        const isCustomLike = !editableFieldIds.includes(colId)
        const next = isCustomLike
          ? (evt.data.custom_values ?? {})[colId]
          : evt.newValue
        // SAM-style: a value-change on a pad row promotes it to a real
        // row. Route through onPromotePadRow → parent creates via POST
        // → next render shows the real row in this pad's place and a
        // fresh pad below. markPendingAdvance retargets focus to that
        // fresh pad's same column once the new row lands.
        if (isPadRowId(evt.data.id)) {
          markPendingAdvance(colId)
          if (onPromotePadRow) onPromotePadRow(evt.data.id, colId, next)
          return
        }
        onCellEdit(evt.data.id, colId, next)
        if (colId === 'quantity') onQuantityCommitted?.()
      },
      [
        onCellEdit,
        editableFieldIds,
        onPromotePadRow,
        markPendingAdvance,
        onQuantityCommitted,
      ],
    )

    const processDataFromClipboard = useCallback(
      (params: ProcessDataFromClipboardParams): string[][] | null => {
        const api = (params.api as GridApi | undefined) ?? apiRef.current
        if (!api) return params.data
        const focused = api.getFocusedCell()
        if (!focused) return params.data
        const colId = focused.column.getColId()
        const row = api.getDisplayedRowAtIndex(focused.rowIndex)
        if (!row?.data?.id || !colId) return params.data
        // Off-load the actual range write to the parent so the persist
        // path (debounced patch buffer + optimistic value plumbing) stays
        // the source of truth.
        onPasteRange(row.data.id, colId, params.data)
        return null
      },
      [onPasteRange],
    )

    // SAM-style right-click → custom popup (lives in the hook).
    const handleCellContextMenu = useAgGridContextMenu(onRowContextMenu)

    const handleCellFocused = useCallback(() => {
      const api = apiRef.current
      if (!api) return
      const focused = api.getFocusedCell()
      if (!focused) {
        onFocusChange(null, null, '')
        return
      }
      const row = api.getDisplayedRowAtIndex(focused.rowIndex)
      const colId = focused.column.getColId()
      if (!row?.data?.id || !colId) {
        onFocusChange(null, null, '')
        return
      }
      const value = String(
        (row.data as unknown as Record<string, unknown>)[colId] ?? '',
      )
      onFocusChange(row.data.id, colId, value)
    }, [onFocusChange])

    // Ctrl/Cmd + D fill-down lives in useAgGridKeyboardShortcuts.
    const handleCellKeyDown = useAgGridKeyboardShortcuts({
      apiRef: apiRef as never,
      editableFieldIds,
      onFillDown,
    })

    return (
      <div
        className="ag-theme-quartz qb-aggrid-with-pads"
        style={{ width: '100%', height: '100%' }}
      >
        <ScopeAgGridStyles />
        <AgGridReact<QuoteLineItem>
          rowData={rows}
          columnDefs={columnDefs as unknown as never}
          context={gridContext}
          defaultColDef={{
            sortable: false,
            resizable: true,
            suppressMovable: false,
          }}
          onGridReady={handleGridReady}
          onCellValueChanged={handleCellValueChanged}
          onCellFocused={handleCellFocused}
          onCellContextMenu={handleCellContextMenu}
          onCellKeyDown={handleCellKeyDown}
          processDataFromClipboard={processDataFromClipboard}
          enableRangeSelection={true}
          suppressCopyRowsToClipboard={true}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          animateRows={false}
          singleClickEdit={false}
          stopEditingWhenCellsLoseFocus={true}
          getRowId={(p) => p.data.id}
          rowClassRules={{
            'qb-pad-row': (p) => isPadRowId(p.data?.id),
          }}
          rowHeight={density === 'compact' ? 24 : 32}
          headerHeight={density === 'compact' ? 28 : 32}
          groupHeaderHeight={density === 'compact' ? 22 : 26}
          suppressClickEdit={!isEditable}
          // Native browser tooltips for per-line notes. AG-Grid's
          // built-in tooltip module requires Enterprise; browser
          // tooltips are free, work on every column with a
          // tooltipValueGetter, and respect the OS-level a11y
          // settings (which the built-in module ignores). See
          // QB-AGGRID-NOTES-DECORATOR-001 for the per-column wiring.
          enableBrowserTooltips={true}
        />
      </div>
    )
  }
  return { default: Inner }
})

/** Public wrapper — Suspense boundary + spinner fallback. */
export default function LazyAgGrid(props: LazyAgGridProps) {
  return (
    <Suspense
      fallback={
        <div className="flex h-full items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/30 text-xs text-slate-600 dark:text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading Excel-grade editor…
        </div>
      }
    >
      <LazyAgGridImpl {...props} />
    </Suspense>
  )
}
